import {
  useTheme,
  useMediaQuery,
  Grid,
  DialogContent,
  DialogActions,
  DialogProps,
  Dialog,
} from '@mui/material';
import { memo } from 'react';
import { styled } from '@mui/styles';
import { PmiButton } from 'shell/components/shared/button';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import PmiTextField from 'shell/components/shared/text-field';
import { useFormik, FormikHelpers } from 'formik';
import AutocompleteMessoList from '../../form/street/messo-list';
import { ItemVM } from 'shell/view-models/item';
import { MunicipalMessengerMessengerListItem } from 'shell/models/municipal-messenger-messenger-list';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { FiltersFormTerritorialArea } from 'system/pages/toponymy/territorial-area/list/types';
import { transformDataset } from '../../../../pages/toponymy/territorial-area/list/utils';
import * as yup from 'yup';

export interface UpdateCivicFormValues {
  officer: ItemVM<MunicipalMessengerMessengerListItem> | null;
  territorialArea: string;
}

export interface UpdateCivicModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (values: UpdateCivicFormValues) => void;
  initialValues?: Partial<UpdateCivicFormValues>;
  filterForm?: FiltersFormTerritorialArea;
}

const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '30rem',
        maxWidth: '40rem',
      },
}));

const UpdateCivicModalComponent = ({
  open,
  onClose,
  onConfirm,
  initialValues,
  filterForm,
}: UpdateCivicModalProps): React.ReactElement => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const updateCivic = useHttpClient(`${__TOPONYMY_API_URL__}/civico/ambito`, {
    options: {
      method: 'PUT',
    },
  });

  const formik = useFormik<UpdateCivicFormValues>({
    initialValues: {
      officer: initialValues?.officer || null,
      territorialArea: initialValues?.territorialArea || '',
    },
    validationSchema: yup.object().shape({
      officer: yup.object().nullable().required('Campo obbligatorio'),
    }),
    onSubmit: (
      values: UpdateCivicFormValues,
      formikHelpers: FormikHelpers<UpdateCivicFormValues>
    ) => {
      (
        updateCivic({
          params: {},
          url: `/${values.officer?.el?.ambitoTerritoriale}`,
          paramsSerializer: () => transformDataset(filterForm),
        }) as ResponsePromise<void>
      ).then(() => {
        onConfirm(values);
        formikHelpers.setSubmitting(false);
      });
    },
  });

  return (
    <PmiDialog open={open} fullScreen={fullScreen} onClose={onClose}>
      <PmiDialogTitle id="update-civic-modal-title" color="white" backgroundColor="primary">
        Valori da aggiornare sui civici
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <AutocompleteMessoList
              label="Messo"
              id="municipalMessenger"
              value={formik.values.officer || null}
              onChange={(value) => formik.setFieldValue('officer', value)}
              required
              error={formik.touched.officer && Boolean(formik.errors.officer)}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <PmiTextField
              label="Ambito territoriale"
              id="territorialArea"
              name="territorialArea"
              value={formik.values.officer?.el?.ambitoTerritoriale || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              disabled
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <PmiButton variant="outlined" color="cdmBrand" onClick={onClose}>
          Annulla
        </PmiButton>
        <PmiButton
          onClick={() => formik.submitForm()}
          variant="contained"
          color="primary"
          disabled={formik.isSubmitting}
        >
          Modifica
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

UpdateCivicModalComponent.displayName = 'Update Civic Modal Component';

const UpdateCivicModal = memo(UpdateCivicModalComponent);

export default UpdateCivicModal;
